import { decode, type EncodeOptions, encode } from "@toon-format/toon";
import { isJsonValue, JsonValue } from "@/graphql/json-value";
import { decodeLegacyToon } from "@/lib/ai/convert/legacy-toon";
import { ConverterError } from "@/lib/ai/error";

const DEFAULT_ENCODE_OPTIONS: EncodeOptions = {
  indentSize: 2,
};

// v4 はドット入りキーを展開しない。旧履歴との判別には TOON のコメント構文を使う。
const TOON_V4_PREFIX = "# amethyst-toon-v4\n";

const JSON_LIKE_PREFIX = /^\s*[\[{]/;
const TOON_HEADER_PATTERN =
  /^(?:\s*)(?:"[^"\n]+"|[A-Za-z_][\w.-]*)(?:\[[^\]\n]+\])?(?:\{[^\}\n]+\})?\s*:/m;
const TOON_LIST_MARKER = /^\s*-\s+/m;
const TOON_ARRAY_LENGTH_PATTERN = /\[[0-9]+(?:[,|\t ]?[^\]]*)?\]/;
const TOON_EMPTY_ARRAY_FIELD_PATTERN =
  /^\s*(?:"[^"\n]+"|[A-Za-z_][\w.-]*)\s*: *\[\] *$/m;
const TOON_FOLDED_FIELD_PATTERN = /^\s*[A-Za-z_]\w*(?:\.[A-Za-z_]\w*)+\s*:/m;
const TOON_INDENTED_FIELD_PATTERN =
  /\n\s{2,}(?:"[^"\n]+"|[A-Za-z_][\w.-]*)\s*:/;
const TOON_ARRAY_HEADER_PATTERN =
  /^\[[0-9]+(?:[,|\t ]?[^\]]*)?\]\{[^\n]+\}\s*:/;

/**
 * JSON値をTOON文字列へ変換する。
 */
export function encodeJsonToToon(value: JsonValue): string {
  if (!isJsonValue(value)) {
    throw new ConverterError("Tool result must be JSON-serializable");
  }
  try {
    return TOON_V4_PREFIX + encode(value, DEFAULT_ENCODE_OPTIONS);
  } catch (error) {
    throw new ConverterError(
      `Failed to encode tool result to TOON: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

/**
 * TOON文字列をJSONへ戻す。
 */
export function decodeToonToJson(input: string): JsonValue {
  try {
    const result = input.startsWith(TOON_V4_PREFIX)
      ? decode(input)
      : decodeLegacyToon(input);
    if (!isJsonValue(result)) {
      throw new ConverterError("Decoded value is not valid JSON");
    }
    return result;
  } catch (error) {
    throw new ConverterError(
      `Failed to decode TOON: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

/**
 * TOONらしさを判定する簡易ヒューリスティック。
 * @see https://github.com/toon-format/toon （配列長やリスト構文などの仕様に基づく）
 */
export function looksLikeToonString(value: string): boolean {
  if (value.startsWith(TOON_V4_PREFIX)) return true;
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (!trimmed.includes(":")) return false;

  const hasHeader = TOON_HEADER_PATTERN.test(trimmed);
  const hasList = TOON_LIST_MARKER.test(trimmed);
  const hasArrayDecl =
    TOON_ARRAY_LENGTH_PATTERN.test(trimmed) ||
    TOON_EMPTY_ARRAY_FIELD_PATTERN.test(trimmed);
  const hasIndentedField = TOON_INDENTED_FIELD_PATTERN.test(trimmed);

  if (
    hasHeader &&
    (hasArrayDecl ||
      hasIndentedField ||
      TOON_FOLDED_FIELD_PATTERN.test(trimmed))
  ) {
    return true;
  }
  if (hasList && (hasIndentedField || hasArrayDecl)) {
    return true;
  }
  if (TOON_ARRAY_HEADER_PATTERN.test(trimmed) && hasArrayDecl) {
    return true;
  }
  if (JSON_LIKE_PREFIX.test(trimmed)) {
    return false;
  }
  return false;
}

/**
 * TOON文字列であれば JSON に復元し、失敗時は undefined を返す。
 */
export function tryDecodeToonString(value: string): JsonValue | undefined {
  if (!looksLikeToonString(value)) {
    return undefined;
  }
  try {
    return decodeToonToJson(value);
  } catch {
    return undefined;
  }
}
